<template>
  <div id="contract-public-card">
    <section
      v-if="notFound"
      class="not-found"
    >
      <header>
        Договор №{{ contract.version }} не найден<br>

        <router-link :to="ContractsPublicService.baseUrl + '/' + contract.type.id">
          Назад
        </router-link>
      </header>
    </section>

    <section
      v-else
      class="contract-form"
    >
      <header>
        <VButton
          class="cancel"
          @click="goBack"
          material="arrow_back_ios"
        >Назад</VButton>

        {{ contract.type.name }}

        <template v-if="isDraft">
          черновик
        </template>

        <template v-else>
          от {{ $core.utils.date.format(contract.publicationDate) }}
        </template>

        <VBadges>
          <VBadge
            v-if="contract.isActual"
            class="primary"
          >
            Актуальная версия
          </VBadge>
          <VBadge
            v-if="contract.publicationDate > today"
            class="warning"
          >
            Не вступил в силу
          </VBadge>
        </VBadges>
      </header>

      <dl class="description">
        <template v-if="contract.publicationDate">
          <dd>Публикация:</dd>
          <dt> {{ $core.utils.date.humanizeDate(contract.publicationDate, null, true) }}</dt>
        </template>

        <template v-if="contract.updatedAt">
          <dd>Обновлен:</dd>
          <dt> {{ $core.utils.date.humanizeDate(contract.updatedAt) }}</dt>
        </template>
        <dd>Создан:</dd>
        <dt> {{ $core.utils.date.humanizeDate(contract.createdAt) }}</dt>


        <dd>Автор:</dd>
        <dt> {{ contract.createdBy?.name ?? 'Неизвестен' }}</dt>
      </dl>

      <VMarkdownEditor
        ref="editor"
        :key="editorKey"
        :value="contract.text ?? ''"
        :readonly="!isDraft"
        @dirty="editorDirty = true"
      />

      <footer>
        <VButton
          v-if="isDraft"
          key="save"
          theme="primary"
          @click="updateContract"
        >
          Сохранить
        </VButton>
        <VButton
          v-if="isDraft"
          key="publish"
          @click="onPublicationStart"
        >
          Публикация
        </VButton>
        <VButton
          :disabled="downloadPDFDisabled"
          @click="ContractsPublicService.getPdf(contract.type, Number(contract.version))"
        >
          <template #icon>
            <VIcon material="picture_as_pdf"/>
          </template>
          Скачать в pdf
        </VButton>

        <VButton
          class="cancel"
          @click="goBack"
        >
          Назад
        </VButton>
      </footer>
    </section>
  </div>
</template>

<style lang="scss" src="./ContractPublicCard.scss"></style>

<script lang="ts">
import ContractsPublicService from '@/domains/Contracts/ContractsPublicService';
import ContractPublic from '@/domains/Contracts/ContractPublic';
import VMarkdownEditor from '@/lib/components/VMarkdownEditor/VMarkdownEditor.vue';

export default defineComponent({
  name: 'ContractPublicCard',

  components: {
    VMarkdownEditor,
  },

  data() {
    return {
      ContractsPublicService,

      /** new делать не нужно, loadContract + fromServer всё сделают */
      contract: {} as ContractPublic,

      notFound: false,
      editorDirty: false,
    };
  },

  computed: {
    isDraft(): boolean {
      return this.contract.isDraft;
    },

    /**
     * Ключ для пересоздания редактора. Меняется при загрузке другой версии
     * и при смене черновик↔опубликовано (readonly читается только при монтировании).
     */
    editorKey(): string {
      return `${this.contract.version}-${this.contract.isDraft}`;
    },

    downloadPDFDisabled(): boolean {
      if (!this.contract.isDraft) {
        return false;
      } else {
        return this.editorDirty;
      }
    },

    today(): Date {
      return this.$core.utils.date.toDayStart(new Date());
    },
  },

  created() {
    const {contractType, contractVersion} = this.$route.params;

    this.contract.type = {
      id: contractType as string,
      name: null,
    } as ContractPublic['type'];

    this.contract.version = contractVersion === 'edit' ? 0 : Number(contractVersion);
  },

  mounted() {
    this.loadContract();
  },

  methods: {
    async loadContract(): Promise<void> {
      const {version, type} = this.contract;

      const contract = await ContractsPublicService.get(
        {id: type.id} as ContractPublic['type'],
        Number(version),
      );

      if (contract) {
        this.contract = contract;
        this.editorDirty = false;
      } else {
        this.notFound = true;
      }
    },

    async updateContract(): Promise<void> {
      this.contract.text = (this.$refs.editor as InstanceType<typeof VMarkdownEditor>).getMarkdown();

      if (await ContractsPublicService.update(this.contract)) {
        await this.loadContract();
      }
    },

    /** После публикации уходим к списку договоров этого типа */
    async onPublicationStart(): Promise<void> {
      if (await this.$core.dialogs.ContractPublicationDialog.showAsPromise({ contract: this.contract }) === true) {
        this.$router.push(`${ContractsPublicService.baseUrl}/${this.contract.type.id}`);
      }
    },

    goBack(): void {
      // если есть куда возвращаться, то туда и возвращаемся
      if (window.history.state?.back) {
        this.$router.back();
        // если истории нет, то в корень, потом переадресация на форму логина
      } else {
        this.$router.push('/');
      }
    },
  },
});
</script>
